import { Col, ColProps, Form, FormInstance, Input, Row, Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { filterValueByCriteria } from '../../../../../shared/utils/filter-value-by-criteria/filter-value-by-criteria';
import { useGetClientGroupsQuery } from '../../../../client-groups/api/client-groups-api';
import { useGetClientUsersRolesQuery } from '../../../api/client-users-api';
import { clientUsersFilterFormId } from '../../../constants/constants';
import { ClientUsersFormFilterModel } from '../../../models/client-user-form-filter-model';
import ClientUsersUnitSelect from '../../client-users-unit-select/client-users-unit-select';

interface Props {
  form: FormInstance<ClientUsersFormFilterModel>;
}

const ClientUserFilterForm = ({ form }: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'client-users']);
  const columnsGrid: ColProps = { xs: 24, sm: 12, md: 6 };
  const [searchParams] = useSearchParams();
  const { data: userRoles } = useGetClientUsersRolesQuery();
  const { data: userGroups } = useGetClientGroupsQuery({
    page: Number(searchParams.get('page')) || 1,
    limit: Number(searchParams.get('limit')) || 30,
  });

  const groupSelectOptions: DefaultOptionType[] = useMemo(() => {
    return (userGroups && Array.isArray(userGroups.items) ? userGroups.items : []).map(userGroup => ({
      value: userGroup.id,
      label: userGroup.name,
    }));
  }, [userGroups]);

  const roleSelectOptions: DefaultOptionType[] = useMemo(() => {
    return (Array.isArray(userRoles) ? userRoles : []).map(userRole => ({
      label: t(`roles.${userRole}`, { ns: 'client-users' }),
      value: userRole,
    }));
  }, [t, userRoles]);

  return (
    <Form id={clientUsersFilterFormId} form={form} layout="vertical">
      <Row gutter={18} align="bottom" justify="end">
        <Col flex="auto">
          <Row gutter={18}>
            <Col {...columnsGrid}>
              <Form.Item name="name" label={t('label.name')}>
                <Input placeholder={t('label.name')} />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="username" label={t('label.username')}>
                <Input placeholder={t('label.username')} />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="email" label={t('label.email')} normalize={value => value.trim()}>
                <Input placeholder={t('label.email')} />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="roles" label={t('label.role')}>
                <Select
                  allowClear={true}
                  showSearch
                  placeholder={t('label.role')}
                  options={roleSelectOptions}
                  filterOption={(inputValue, option) =>
                    filterValueByCriteria((option?.label ?? '').toString(), inputValue)
                  }
                />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="unit" label={t('label.unit')}>
                <ClientUsersUnitSelect />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="groups" label={t('label.groups')}>
                <Select
                  allowClear={true}
                  showSearch
                  placeholder={t('label.groups')}
                  options={groupSelectOptions}
                  filterOption={(inputValue, option) =>
                    filterValueByCriteria((option?.label ?? '').toString(), inputValue)
                  }
                />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="signature" label={t('label.signature', { ns: 'client-users' })}>
                <Select
                  defaultActiveFirstOption={false}
                  allowClear={true}
                  options={[
                    {
                      value: '0',
                      label: t('label.options.false'),
                    },
                    {
                      value: '1',
                      label: t('label.options.true'),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col {...columnsGrid}>
              <Form.Item name="active" label={t('label.active', { ns: 'client-users' })}>
                <Select
                  defaultActiveFirstOption={false}
                  allowClear={true}
                  options={[
                    {
                      value: '0',
                      label: t('label.options.false'),
                    },
                    {
                      value: '1',
                      label: t('label.options.true'),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};
export default ClientUserFilterForm;
